import type { ScheduledOnOff, ShutdownSettings } from "@/views/appLauncher/lib/types"
import { Workspace } from "@/services"
import { ref, reactive } from "@vue/composition-api"
import { defineStore } from "pinia"

export interface TempSettings {
  scheduledOnOff: ScheduledOnOff | null
  shutdownSettings: ShutdownSettings | null
}

export const scheduleModule = defineStore("space.scheduleModule", () => {
  const scheduledOnOff = ref<ScheduledOnOff>({
    scheduledOnOffEnabled: false,
    autoStartupAt: null,
    autoShutdownAt: null
  })

  const shutdownSettings = ref<ShutdownSettings>({
    autoShutdownEnabled: false,
    shutdownTimerInHours: 2,
    inactivityTrackerEnabled: false,
    inactivityPeriodInHours: 0
  })

  const temp = reactive<TempSettings>({
    scheduledOnOff: null,
    shutdownSettings: null
  })

  const getShutdownSettings = async (spaceId: string) => {
    const req = await Workspace.getAutoShutdownSettings(spaceId)
    shutdownSettings.value = req.data.data
  }

  const getScheduledOnOff = async (spaceId: string) => {
    const req = await Workspace.getScheduledOnOff(spaceId)

    if (req.data.data) {
      scheduledOnOff.value = req.data.data
      return scheduledOnOff.value
    } else {
      cleanScheduleScope()
    }
  }

  const startEdit = async (spaceId: string) => {
    await updateData(spaceId)

    temp.scheduledOnOff = { ...scheduledOnOff.value }
    temp.shutdownSettings = { ...shutdownSettings.value }
  }

  const disableSchedule = async (spaceId: string) => {
    await Promise.all([
      Workspace.cancelAutoShutdown(spaceId),
      Workspace.setScheduledOnOff(spaceId, {
        autoShutdownAt: null,
        autoStartupAt: null,
        scheduledOnOffEnabled: false
      }),
      Workspace.setShutdownSettings(spaceId, {
        ...shutdownSettings.value,
        autoShutdownEnabled: false
      })
    ])
  }

  const updateData = async (spaceId: string) => {
    return await Promise.all([getShutdownSettings(spaceId), getScheduledOnOff(spaceId)])
  }

  const save = async (spaceId: string) => {
    _fixTempData()

    if (temp.scheduledOnOff) {
      const req = await Workspace.setScheduledOnOff(spaceId, temp.scheduledOnOff)

      if (req.data.data) {
        scheduledOnOff.value = req.data.data
      }
    }
    if (temp.shutdownSettings) {
      const req = await Workspace.setShutdownSettings(spaceId, temp.shutdownSettings)

      if (req.data.data) {
        shutdownSettings.value = req.data.data
      }
    }
  }

  const reset = () => {
    const settings = temp.shutdownSettings as ShutdownSettings
    const schedule = temp.scheduledOnOff as ScheduledOnOff

    if (settings) {
      settings.autoShutdownEnabled = false
    }
    if (schedule) {
      schedule.scheduledOnOffEnabled = false
      schedule.autoShutdownAt = null
      schedule.autoStartupAt = null
    }
  }

  const cleanScheduleScope = () => {
    scheduledOnOff.value.scheduledOnOffEnabled = false
    scheduledOnOff.value.autoStartupAt = null
    scheduledOnOff.value.autoShutdownAt = null

    if (temp.scheduledOnOff) {
      temp.scheduledOnOff = null
    }
    if (temp.shutdownSettings) {
      temp.shutdownSettings = null
    }
  }

  const _fixTempData = () => {
    const schedule = temp.scheduledOnOff as ScheduledOnOff
    const settings = temp.shutdownSettings as ShutdownSettings

    if (schedule && settings) {
      if (schedule.scheduledOnOffEnabled) {
        settings.autoShutdownEnabled = false
        settings.inactivityPeriodInHours = 0
        settings.inactivityTrackerEnabled = false
        settings.shutdownTimerInHours = 2
      } else if (settings.autoShutdownEnabled) {
        schedule.autoShutdownAt = null
        schedule.autoStartupAt = null
        schedule.scheduledOnOffEnabled = false
      }
    }
  }

  return {
    reset,
    cleanScheduleScope,
    disableSchedule,
    temp,
    updateData,
    startEdit,
    save,
    getShutdownSettings,
    getScheduledOnOff,
    scheduledOnOff,
    shutdownSettings
  }
})
